import React, { useState, useEffect, useCallback } from "react";
import { Button, Loading, Text, Textarea, Modal } from '@nextui-org/react';
import { Delete } from 'react-iconly';

import endpoint from "../../config/endpoint.json";
import { success, error } from "../../lib/userAlerts";
import { secureFetch } from "../../lib/api";

const LocationDetails = ({
  selectedLocation,
  deleteCommentFromState,
  toggleShowDetails
}) => {
  // State
  const [note, setNote] = useState(selectedLocation.locationNotes || "");
  const [addressBackgroundColor, setAddressBackgroundColor] = useState("yellow");
  const [googleMatchReview, setGoogleMatchReview] = useState("");
  const [action, setAction] = useState("");
  const [latitide, setLatitide] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [googleAddress, setGoogleAddress] = useState("");
  const [override, setOverride] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [visible, setVisible] = useState(false);

  // Set initial status and address
  useEffect(() => {
    if (selectedLocation.locationStatusId === 5) {
      setAddressBackgroundColor("green");
      setAction("verified");
    } else if (selectedLocation.locationStatusId === 6) {
      setAddressBackgroundColor("red");
      setAction("incorrect");
    } else if (selectedLocation.locationStatusId === 7) {
      setAddressBackgroundColor("blue");
      setAction("override");
    } else {
      setAddressBackgroundColor("yellow");
      setAction("unverified");
    }
    setAddress();
    // eslint-disable-next-line
  }, [selectedLocation]);

  // Set address based on status
  const setAddress = useCallback(() => {
    if (
      !selectedLocation.locationStatusId ||
      selectedLocation.locationStatusId === 1 ||
      selectedLocation.locationStatusId === 2
    ) {
      checkAddress();
    } else {
      setGoogleAddress(selectedLocation.gAddress);
      setLatitide(selectedLocation.oLat || selectedLocation.gLat);
      setLongitude(selectedLocation.oLong || selectedLocation.gLong);
    }
    // eslint-disable-next-line
  }, [selectedLocation]);

  // Check address with Google
  const checkAddress = useCallback(() => {
    const address = `${selectedLocation.address},${selectedLocation.city},${selectedLocation.state},${selectedLocation.country},${selectedLocation.zip}`;
    secureFetch(endpoint.checkAddress, { address }).then(
      ([ok, status, json]) => {
        if (ok) {
          let body = JSON.parse(json.gAddressDetails);
          if (body.results.length === 0) {
            setGoogleMatchReview("| Consider updating your source system");
            setGoogleAddress("Not available");
            setLatitide("Not available");
            setLongitude("Not available");
            updateGoogleAddress(null);
          } else if (body.results[0].partial_match) {
            setGoogleMatchReview("| Partial match in google");
            setGoogleAddress(body.results[0].formatted_address);
            setLatitide(body.results[0].geometry.location.lat);
            setLongitude(body.results[0].geometry.location.lng);
            updateGoogleAddress(body.results[0]);
          } else {
            setGoogleMatchReview("| Complete match in google");
            setGoogleAddress(body.results[0].formatted_address);
            setLatitide(body.results[0].geometry.location.lat);
            setLongitude(body.results[0].geometry.location.lng);
            updateGoogleAddress(body.results[0]);
          }
        } else {
          error("Oops! error occured during fetch address from google");
        }
      }
    );
    // eslint-disable-next-line
  }, [selectedLocation]);

  // Update Google address in backend
  const updateGoogleAddress = useCallback((googleResponse, isReset) => {
    let gbody = {
      gAddress: "",
      gCity: "",
      gState: "",
      gZip: "",
      gCountry: "",
      gLat: 0,
      gLong: 0,
      locationMatchStatusId: isReset ? 1 : 2,
      stopId: selectedLocation.stopId,
    };

    if (googleResponse) {
      gbody.gAddress = googleResponse.formatted_address;
      gbody.gLat = googleResponse.geometry.location.lat;
      gbody.gLong = googleResponse.geometry.location.lng;
      gbody.locationMatchStatusId = googleResponse.partial_match ? 4 : 3;

      googleResponse.address_components.forEach((element) => {
        if (element.types[0] === "administrative_area_level_2") {
          gbody.gCity = element.short_name;
        }
        if (element.types[0] === "administrative_area_level_1") {
          gbody.gState = element.short_name;
        }
        if (element.types[0] === "country") {
          gbody.gCountry = element.short_name;
        }
        if (element.types[0] === "postal_code") {
          gbody.gZip = element.short_name;
        }
      });
    }

    secureFetch(endpoint.updateGoogleAddress, gbody).then(
      ([ok, status, json]) => {
        if (ok) {
          success(json.message);
          if (isReset) {
            checkAddress();
          }
        } else {
          error("Oops! error occured during update google address");
        }
      }
    );
    // eslint-disable-next-line
  }, [selectedLocation, checkAddress]);

  // Update Google address status
  const updateGoogleAddressStatus = useCallback((oLat, oLong, statusId) => {
    let statusBody = {
      oLat: oLat,
      oLong: oLong,
      locationMatchStatusId: statusId,
      stopId: selectedLocation.stopId,
    };

    secureFetch(endpoint.updateGoogleAddressStatus, statusBody).then(
      ([ok, status, json]) => {
        if (ok) {
          success(json.message);
        } else {
          error("Oops! error occured during update address status");
        }
      }
    );
  }, [selectedLocation]);

  // Location verify section
  const locationVerifySection = () => {
    let addressBackgroundColorClass = "";
    if (addressBackgroundColor === "red") {
      addressBackgroundColorClass = "bg-light-red mt2 pa2 w-90";
    } else if (addressBackgroundColor === "green") {
      addressBackgroundColorClass = "bg-light-green mt2 pa2 w-90";
    } else if (addressBackgroundColor === "blue") {
      addressBackgroundColorClass = "bg-light-blue mt2 pa2 w-90";
    } else {
      addressBackgroundColorClass = "bg-light-yellow mt2 pa2 w-90";
    }
    let text = [
      selectedLocation.address,
      selectedLocation.city,
      selectedLocation.state,
      selectedLocation.country,
      selectedLocation.zip,
    ]
      .filter(Boolean)
      .join(", ");
    return (
      <div className="flex flex-column w-50">
        <div className="flex items-center w-90">
          <div className="f3">{selectedLocation.stopId}</div>
          <div className="f5 ml2">{text}</div>
        </div>
        {override ? (
          <div className="mt2 pa2 w-90 flex ba">
            <div className="w-50">
              <div className="flex items-center">
                <div className="w-40 f5">Latitude</div>
                <input
                  type="number"
                  placeholder="Enter latitude"
                  onChange={e => setLatitide(e.target.value)}
                  style={{ width: "50%" }}
                  value={latitide}
                />
              </div>
              <div className="flex items-center mt2">
                <div className="w-40 f5">Longitude</div>
                <input
                  type="number"
                  placeholder="Enter longitude"
                  onChange={e => setLongitude(e.target.value)}
                  style={{ width: "50%" }}
                  value={longitude}
                />
              </div>
            </div>
            <div className="w-50">
              Be careful! Drivers will receive information over their phone when
              they get close to the coordinates you enter!
            </div>
          </div>
        ) : (
          <div className={addressBackgroundColorClass}>
            <div className="f5">
              Google Match - <span className="fw6"> {action} </span> {googleMatchReview}
            </div>
            <div className="f5">{googleAddress}</div>
            <div className="f5">
              Latitide: {latitide} | Longitude: {longitude}
            </div>
          </div>
        )}

        <div className="flex items-center mt2">
          <Button
            auto
            ghost
            color='success'
            css={{ br: 0, mr: 10 }}
            onClick={() => {
              updateGoogleAddressStatus(null, null, 5);
              setOverride(false);
              setAddressBackgroundColor("green");
              setAction("verified");
            }}
          >
            Verify
          </Button>
          <Button
            auto
            ghost
            color="error"
            css={{ br: 0, mr: 10 }}
            onClick={() => {
              updateGoogleAddressStatus(null, null, 6);
              setOverride(false);
              setAddressBackgroundColor("red");
              setAction("incorrect");
            }}
          >
            Incorrect Match
          </Button>
          <Button
            auto
            ghost
            css={{ br: 0, mr: 10 }}
            onClick={() => {
              if (override) {
                updateGoogleAddressStatus(latitide, longitude, 7);
              }
              setOverride(true);
              setAction("verified");
            }}
          >
            Override GPS Location
          </Button>
          <Button
            auto
            ghost
            color="secondary"
            css={{ br: 0, mr: 10 }}
            onClick={() => {
              updateGoogleAddress(null, true);
              setOverride(false);
              setAddressBackgroundColor("yellow");
              setAction("unverified");
            }}>
            Reset
          </Button>
        </div>
      </div>
    );
  };

  // Delete review
  const deleteReview = (value) => {
    setDeleting(true);
    secureFetch(endpoint.updateDriverNotes, {
      status: 3,
      noteId: value.id,
    }).then(([ok, status, json]) => {
      if (ok) {
        success(json.message);
        setDeleting(false);
        setVisible(false);
        deleteCommentFromState(value);
      } else {
        setDeleting(false);
        setVisible(false);
        error("Oops! error occured during delete review!");
      }
    });
  };

  // Update driver location note
  const updateDriverLocationNote = () => {
    setSubmitting(true);
    secureFetch(endpoint.updateDriverLocationNote, {
      note: note,
      stopId: selectedLocation.stopId,
    }).then(([ok, status, json]) => {
      if (ok) {
        setSubmitting(false);
        success(json.message);
      } else {
        setSubmitting(false);
        error("Oops! error occured during update location note!");
      }
    });
  };

  // Toggle delete modal
  const toggleDeleteVisibility = () => setVisible(true);

  // Location info section
  const locationInfoSection = () => {
    let driverNotes = [];
    try {
      driverNotes = JSON.parse(selectedLocation.comments);
    } catch {
      driverNotes = [];
    }
    return (
      <div className="flex">
        <div className="w-50">
          <div className="f3 mt2">Driver Comments To Add</div>
          <div
            style={{ height: 500 }}
            className="bg-light-gray flex flex-column f6 pa2 mt2 overflow-x-scroll"
          >
            {driverNotes && driverNotes.length > 0 ? (
              driverNotes.map((item) => (
                <div
                  key={item.id}
                  style={{ width: "100%" }}
                  className="flex h4 bg-white br2 items-center pa3 mt2"
                >
                  <div className="f6" style={{ width: "90%" }}>
                    {item.driverNote}
                  </div>
                  <div
                    style={{ width: "10%" }}
                    className="flex justify-center items-center"
                  >
                    <Button
                      auto
                      color='error'
                      css={{ br: 0 }}
                      iconSize={20}
                      onClick={toggleDeleteVisibility}
                      icon={<Delete fill="currentColor" />}
                    />
                    <Modal
                      closeButton
                      blur
                      css={{ br: 0 }}
                      open={visible}
                      position='right'
                    >
                      <Modal.Header>
                        <Text id="modal-title" size={18}>
                          Delete this Driver Comment
                        </Text>
                      </Modal.Header>
                      <Modal.Body>
                        <Text b size={14}>Please confirm if you want to delete this comment? This action is irreversible.</Text>
                      </Modal.Body>
                      <Modal.Footer>
                        <Button
                          auto
                          css={{ br: 0 }}
                          color='error'
                          onClick={() => deleteReview(item)}>
                          {deleting ? (<Loading type="points" color="white" size="md" />) : ('YES ')}
                        </Button>
                      </Modal.Footer>
                    </Modal>
                  </div>
                </div>
              ))
            ) : (
              <div className="f5 ">Comments not available</div>
            )}
          </div>
        </div>
        <div className="w-50 pl3">
          <div className="flex justify-between items-center">
            <div className="f3">Location Info</div>
          </div>
          <Textarea
            bordered
            fullWidth
            css={{ br: 0, mt: 10, br: 0 }}
            rows={10}
            placeholder="Enter your comment"
            onChange={e => setNote(e.target.value)}
            value={note}
          />
          <div className="flex justify-end f6 mt2 pa2 w-100">
            <Button
              auto
              ghost
              css={{ br: 0, mr: 10 }}
              className="ba w4"
              onClick={() => {
                setOverride(false);
                toggleShowDetails();
              }}
            >
              Close
            </Button>
            <Button
              auto
              css={{ br: 0 }}
              onClick={updateDriverLocationNote}
            >
              {submitting ? <Loading type="points" color="white" size="md" /> : 'Submit'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Render
  return (
    <div className="pa3 w-100">
      <div className="w-100">
        <div>{locationVerifySection()}</div>
        <div className="bb mt3 mb2"></div>
        <div>{locationInfoSection()}</div>
      </div>
    </div>
  );
};

export default LocationDetails;